'use client'

import { useState, useCallback, useMemo, useRef } from 'react'
import type { 
  UseBulkSelectionReturn, 
  UseBulkSelectionOptions 
} from './types'
import { safeAccess } from '../../utils/security'

/**
 * useBulkSelection - Hook for managing bulk selection with BIRHAUS principles
 * 
 * Features:
 * - Miller's Law compliance (max selection limits)
 * - Undo/redo support for selection operations
 * - Range selection for efficiency
 * - Filter-based selection
 * - Persistent selection across data changes
 */
/* eslint-disable @typescript-eslint/no-explicit-any, max-lines-per-function */
// Bulk selection requires flexible data handling and comprehensive selection logic
export function useBulkSelection<T extends Record<string, any>>(
  options: UseBulkSelectionOptions = {}
): UseBulkSelectionReturn<T> {
  const {
    maxSelection = Number.MAX_SAFE_INTEGER,
    persistSelection: _persistSelection = false,
    keyField = 'id'
  } = options

  const [selectedItems, setSelectedItems] = useState<T[]>([])
  
  // BIRHAUS Principle: Undo/redo support for selection operations
  const selectionHistory = useRef<T[][]>([])
  const historyIndex = useRef(-1)
  
  // Helper function to add to history
  const addToHistory = useCallback((newSelection: T[]) => {
    // Remove any future history if we're not at the end
    selectionHistory.current = selectionHistory.current.slice(0, historyIndex.current + 1)
    
    // Add new state
    selectionHistory.current.push([...newSelection])
    historyIndex.current = selectionHistory.current.length - 1
    
    // Limit history size for performance (Miller's Law: keep it manageable)
    if (selectionHistory.current.length > 50) {
      selectionHistory.current = selectionHistory.current.slice(-25)
      historyIndex.current = selectionHistory.current.length - 1
    }
  }, [])

  // Update selection with history tracking
  const updateSelection = useCallback((newSelection: T[]) => {
    const currentSelectionJson = JSON.stringify(selectedItems.map(item => safeAccess(item, String(keyField), '')).sort())
    const newSelectionJson = JSON.stringify(newSelection.map(item => safeAccess(item, String(keyField), '')).sort())
    
    // Only add to history if selection actually changed
    if (currentSelectionJson !== newSelectionJson) {
      addToHistory(selectedItems) // Save current state before changing
      setSelectedItems(newSelection)
    }
  }, [selectedItems, keyField, addToHistory])

  const selectItem = useCallback((item: T) => {
    setSelectedItems(prev => {
      const exists = prev.some(i => safeAccess(i, String(keyField), '') === safeAccess(item, String(keyField), ''))
      if (exists) return prev
      
      // BIRHAUS Principle: Miller's Law - limit selection size for cognitive load
      if (prev.length >= maxSelection) {
        console.warn(`BIRHAUS Warning: Maximum selection limit (${maxSelection}) reached. Consider pagination or filtering.`)
        return prev
      }
      
      const newSelection = [...prev, item]
      addToHistory(prev)
      return newSelection
    })
  }, [maxSelection, keyField, addToHistory])

  const deselectItem = useCallback((itemId: string | number) => {
    setSelectedItems(prev => {
      const newSelection = prev.filter(item => safeAccess(item, String(keyField), '') !== itemId)
      if (newSelection.length !== prev.length) {
        addToHistory(prev)
      }
      return newSelection
    })
  }, [keyField, addToHistory])

  const selectAll = useCallback((items: T[]) => {
    // BIRHAUS Principle: Miller's Law compliance
    const limitedItems = items.slice(0, maxSelection)
    if (items.length > maxSelection) {
      console.warn(`BIRHAUS Warning: Selecting only first ${maxSelection} items due to cognitive load limits.`)
    }
    
    updateSelection(limitedItems)
  }, [maxSelection, updateSelection])

  const clearSelection = useCallback(() => {
    updateSelection([])
  }, [updateSelection])

  const isSelected = useCallback((itemId: string | number) => {
    return selectedItems.some(item => safeAccess(item, String(keyField), '') === itemId)
  }, [selectedItems, keyField])

  const toggleSelection = useCallback((item: T) => {
    const itemId = safeAccess(item, String(keyField), '')
    if (isSelected(itemId)) {
      deselectItem(itemId)
    } else {
      selectItem(item)
    }
  }, [isSelected, selectItem, deselectItem, keyField])

  const isAllSelected = useCallback((totalItems: T[]) => {
    if (totalItems.length === 0) return false
    
    const effectiveTotal = Math.min(totalItems.length, maxSelection)
    return selectedItems.length === effectiveTotal && 
           totalItems.slice(0, effectiveTotal).every(item => isSelected(safeAccess(item, String(keyField), '')))
  }, [selectedItems.length, maxSelection, isSelected, keyField])

  const getSelectionStats = useCallback(() => {
    return {
      selected: selectedItems.length,
      total: selectedItems.length, // This would be passed from parent component
      percentage: 0, // Would be calculated by parent
      isMaxReached: selectedItems.length >= maxSelection
    }
  }, [selectedItems.length, maxSelection])

  // BIRHAUS Enhancement: Range selection for efficiency
  const selectRange = useCallback((startIndex: number, endIndex: number, items: T[]) => {
    const start = Math.min(startIndex, endIndex)
    const end = Math.max(startIndex, endIndex)
    const rangeItems = items.slice(start, end + 1)
    
    // Merge with existing selection (avoid duplicates)
    const existingIds = new Set(selectedItems.map(item => safeAccess(item, String(keyField), '')))
    const newItems = rangeItems.filter(item => !existingIds.has(safeAccess(item, String(keyField), '')))
    
    const combined = [...selectedItems, ...newItems]
    const limited = combined.slice(0, maxSelection)
    
    updateSelection(limited)
  }, [selectedItems, keyField, maxSelection, updateSelection])

  // BIRHAUS Enhancement: Filter-based selection
  const selectByFilter = useCallback((predicate: (item: T) => boolean, items: T[]) => {
    const filteredItems = items.filter(predicate)
    const existingIds = new Set(selectedItems.map(item => safeAccess(item, String(keyField), '')))
    const newItems = filteredItems.filter(item => !existingIds.has(safeAccess(item, String(keyField), '')))
    
    const combined = [...selectedItems, ...newItems]
    const limited = combined.slice(0, maxSelection)
    
    updateSelection(limited)
  }, [selectedItems, keyField, maxSelection, updateSelection])

  // BIRHAUS Principle: Undo/redo support
  const undo = useCallback(() => {
    if (historyIndex.current > 0) {
      historyIndex.current--
      const previousSelection = selectionHistory.current[historyIndex.current]
      setSelectedItems([...previousSelection])
    }
  }, [])

  const redo = useCallback(() => {
    if (historyIndex.current < selectionHistory.current.length - 1) {
      historyIndex.current++
      const nextSelection = selectionHistory.current[historyIndex.current]
      setSelectedItems([...nextSelection])
    }
  }, [])

  const canUndo = useMemo(() => {
    return historyIndex.current > 0
  }, [selectedItems]) // Depend on selectedItems to trigger recalculation

  const canRedo = useMemo(() => {
    return historyIndex.current < selectionHistory.current.length - 1
  }, [selectedItems]) // Depend on selectedItems to trigger recalculation

  return {
    selectedItems,
    selectedCount: selectedItems.length,
    selectItem,
    deselectItem,
    selectAll,
    clearSelection,
    isSelected,
    toggleSelection,
    isAllSelected,
    getSelectionStats,
    selectRange,
    selectByFilter,
    undo,
    redo,
    canUndo,
    canRedo
  }
}

/**
 * useBulkOperations - Hook for managing bulk operations with progress tracking
 */
export function useBulkOperations<T>() {
  const [isExecuting, setIsExecuting] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [error, setError] = useState<Error | null>(null)
  const [cancelled, setCancelled] = useState(false)
  
  const executeWithProgress = useCallback(async (
    items: T[],
    operation: (item: T, index: number) => Promise<void>,
    options: {
      batchSize?: number
      onProgress?: (current: number, total: number) => void
      onError?: (error: Error, item: T, index: number) => void
    } = {}
  ) => {
    const { batchSize = 10, onProgress, onError } = options
    
    setIsExecuting(true)
    setProgress({ current: 0, total: items.length })
    setError(null)
    setCancelled(false)
    
    try {
      // Process items in batches to prevent UI blocking
      for (let i = 0; i < items.length; i += batchSize) {
        if (cancelled) break
        
        const batch = items.slice(i, Math.min(i + batchSize, items.length))
        
        await Promise.allSettled(
          batch.map(async (item, batchIndex) => {
            const globalIndex = i + batchIndex
            try {
              await operation(item, globalIndex)
              
              setProgress(prev => {
                const newCurrent = prev.current + 1
                onProgress?.(newCurrent, prev.total)
                return { ...prev, current: newCurrent }
              })
            } catch (err) {
              const error = err instanceof Error ? err : new Error(String(err))
              onError?.(error, item, globalIndex)
              setError(error)
            }
          })
        )
        
        // Small delay between batches to allow UI updates
        await new Promise(resolve => setTimeout(resolve, 10))
      }
    } finally {
      setIsExecuting(false)
      if (!cancelled) {
        setProgress({ current: 0, total: 0 })
      }
    }
  }, [cancelled])
  
  const cancel = useCallback(() => {
    setCancelled(true)
  }, [])
  
  const reset = useCallback(() => {
    setIsExecuting(false)
    setProgress({ current: 0, total: 0 })
    setError(null)
    setCancelled(false)
  }, [])
  
  return {
    isExecuting,
    progress,
    error,
    cancelled,
    executeWithProgress,
    cancel,
    reset
  }
}

/**
 * useBulkActionValidation - Hook for validating bulk actions according to BIRHAUS principles
 */
export function useBulkActionValidation<T>() {
  const validateMillersLaw = useCallback((actionsCount: number) => {
    return actionsCount <= 4 // BIRHAUS Miller's Law limit
  }, [])
  
  const validateSpanishFirst = useCallback((actions: Array<{ labelEs?: string; labelEn?: string }>) => {
    return actions.every(action => action.labelEs && action.labelEs.length > 0)
  }, [])
  
  const validateSelectionSize = useCallback((selectedCount: number, maxRecommended = 100) => {
    if (selectedCount === 0) {
      return { valid: false, message: 'No items selected' }
    }
    
    if (selectedCount > maxRecommended) {
      return { 
        valid: false, 
        message: `Selection too large (${selectedCount} items). Consider filtering or pagination.` 
      }
    }
    
    return { valid: true }
  }, [])
  
  return {
    validateMillersLaw,
    validateSpanishFirst,
    validateSelectionSize
  }
}